import "react-datepicker/dist/react-datepicker.css";
import { Button, Card } from "react-bootstrap";
import { getConfigWithToken } from "../config/config";
import { toast, ToastContainer } from "react-toastify";
import { useEffect, useState, useRef, useCallback } from "react";
import { useSelector } from "react-redux";
import axios from "axios";
import { getFilteredSessions, getSortedSessions, getVerificationStatus } from "../components/UpcomingSessions/utils";
import SessionFilters from "../components/UpcomingSessions/SessionFilters";
import SessionsTable from "../components/UpcomingSessions/SessionsTable";
import SessionPagination from "../components/UpcomingSessions/SessionPagination";
import SessionActionsDropdown from "../components/UpcomingSessions/SessionActionsDropdown";
import SessionDetailsDrawer from "../components/UpcomingSessions/SessionDetailsDrawer";

const SessionBillingDetails = () => {
    const [appointmentsPerPage, setAppointmentsPerPage] = useState(20);
    const [currentPage, setCurrentPage] = useState(1);
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [paginateAppointments, setPaginateAppointments] = useState([]);
    const [search, setSearch] = useState("");
    const [selectedEndDate, setSelectedEndDate] = useState(null);
    const [selectedSession, setSelectedSession] = useState(null);
    const [selectedStartDate, setSelectedStartDate] = useState(null);
    const [sessions, setSessions] = useState([]);
    const [sessionsLoading, setSessionsLoading] = useState(false);
    const [totalSessions, setTotalSessions] = useState(0);
    const [statusFilter, setStatusFilter] = useState("All");
    const [insuranceStatusFilter, setInsuranceStatusFilter] = useState("All");
    const [paymentMethodFilter, setPaymentMethodFilter] = useState("All");
    const [openDropdownId, setOpenDropdownId] = useState(null);
    const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
    const dropdownButtonRef = useRef(null);
    const dropdownMenuRef = useRef(null);
    const [paymentDataBySession, setPaymentDataBySession] = useState({});
    const [showDetailsDrawer, setShowDetailsDrawer] = useState(false);
    const [sortConfig, setSortConfig] = useState({ key: "date", direction: "desc" });
    const [showDatePicker, setShowDatePicker] = useState(false)

    const user = useSelector((state) => state?.user);

    const logAuditEvent = (event, sessionId, data = {}) => {
        const auditLog = {
            event,
            sessionId: sessionId || "N/A",
            timestamp: new Date().toISOString(),
            updatedBy: user?.user?._id || user?.user?.id || "admin_001",
            ...data,
        };
    };

    const clearSearchFilter = () => {
        setDebouncedSearch("");
        setSearch("");
    };

    const fetchSessions = useCallback(async () => {
        let timeoutId = null;
        try {
            setSessionsLoading(true);

            const params = {};
            const formatDate = (date) => {
                const y = date.getFullYear();
                const m = String(date.getMonth() + 1).padStart(2, "0");
                const d = String(date.getDate()).padStart(2, "0");
                return `${y}-${m}-${d}`;
            };

            if (selectedStartDate && selectedEndDate) {
                params.startDate = formatDate(selectedStartDate);
                params.endDate = formatDate(selectedEndDate);

            } else if (selectedStartDate && !selectedEndDate) {
                params.date = formatDate(selectedStartDate);
            }

            if (debouncedSearch && debouncedSearch.trim() !== "") {
                params.search = debouncedSearch.trim();
                params.searchAll = true;
            }

            params.billing = true;
            params.lightweight = true;
            params.limit = appointmentsPerPage;
            params.skip = (currentPage - 1) * appointmentsPerPage;

            timeoutId = setTimeout(() => setSessionsLoading(false), 60000);

            const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}/appointment/getSessionHistory`,
                { params, ...getConfigWithToken(user.user?.token), timeout: 30000 }
            );

            if (response.data.success) {
                const sessionsData = response.data.data ?? [];

                const transformedSessions = sessionsData.map((session) => {
                    const sessionId = session._id ?? session.id ?? Math.random().toString();
                    let paymentData = session.paymentData ?? null;

                    if (paymentData?.paymentMethod === "access code") {
                        paymentData = {
                            ...paymentData,
                            amount: 0,
                            status: "Paid",
                        };
                    }

                    return {
                        ...session,
                        _id: sessionId,
                        appointmentdate: session.appointmentdate ?? session.date ?? session.appointmenttime,
                        appointmenttime: session.appointmenttime ?? session.appointmentdate ?? session.date,
                        status: session.status ?? "Scheduled",
                        paymentData,
                    };
                });

                setSessions(transformedSessions);
                const serverTotal =
                    response.data.total ?? response.data.totalCount ?? null;
                setTotalSessions(
                    Number.isFinite(serverTotal) ? serverTotal : transformedSessions.length
                );

                const paymentDataMap = {};
                transformedSessions.forEach((session) => {
                    paymentDataMap[session._id] = session.paymentData;
                });
                setPaymentDataBySession(paymentDataMap);

                if (timeoutId) clearTimeout(timeoutId);
                setSessionsLoading(false);
            } else {
                if (timeoutId) clearTimeout(timeoutId);
                toast.error("Failed to fetch sessions.");
                setSessionsLoading(false);
            }
        } catch (error) {
            if (timeoutId) clearTimeout(timeoutId);
            if (error.code === "ECONNABORTED" || error.message === "Network Error") {
                toast.error("Network connection lost. Please check your connection and try again.");
            } else {
                toast.error(error?.response?.data?.message || "Failed to fetch sessions");
            }
            setSessionsLoading(false);
        }
    }, [
        debouncedSearch,
        selectedStartDate,
        selectedEndDate,
        user.user?.token,
        currentPage,
        appointmentsPerPage,
    ]);

    const handleSort = (key) => {
        let direction = "desc";
        if (sortConfig.key === key && sortConfig.direction === "desc") {
            direction = "asc";
        }
        setSortConfig({ key, direction });
    };

    const handleRangeSelect = (dates) => {
        if (!dates) {
            setSelectedStartDate(null);
            setSelectedEndDate(null);
            setCurrentPage(1);
            return;
        }

        const [start, end] = dates;
        setSelectedStartDate(start);
        setSelectedEndDate(end ? end : null);
        if (end) setShowDatePicker(false);
    };

    const handleViewDetails = async (session) => {
        try {
            const appointmentId = session._id || session.id;

            if (!appointmentId) {
                toast.error("Appointment ID is missing");
                return;
            }
            if (session.paymentData && !paymentDataBySession[appointmentId]) {
                setPaymentDataBySession((prev) => ({
                    ...prev,
                    [appointmentId]: session.paymentData,
                }));
            }
            setSelectedSession(session);
            setShowDetailsDrawer(true);
            logAuditEvent("session_viewed", appointmentId);
        } catch (error) {
            console.error("Error in handleViewDetails:", error);
            toast.error("Error loading session details");
        }
    };

    const calculateDropdownPosition = useCallback((buttonRect, dropdownHeight = 160, dropdownWidth = 160) => {
        const viewportHeight = window.innerHeight;
        const viewportWidth = window.innerWidth;
        const spaceBelow = viewportHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;

        let top, left;
        if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
            top = buttonRect.top - dropdownHeight - 4;
            if (top < 0) {
                top = 4;
            }
        } else {
            top = buttonRect.bottom + 4;
            if (top + dropdownHeight > viewportHeight) {
                top = viewportHeight - dropdownHeight - 4;
                if (top < buttonRect.top - dropdownHeight) {
                    top = Math.max(4, buttonRect.top - dropdownHeight - 4);
                }
            }
        }

        left = buttonRect.left + buttonRect.width / 2;
        if (left - dropdownWidth / 2 < 0) {
            left = dropdownWidth / 2 + 4;
        } else if (left + dropdownWidth / 2 > viewportWidth) {
            left = viewportWidth - dropdownWidth / 2 - 4;
        }

        return { top, left };
    }, []);

    const handleDropdownToggle = useCallback((sessionId, event) => {
        if (openDropdownId === sessionId) {
            setOpenDropdownId(null);
            dropdownButtonRef.current = null;
        } else {
            const button = event.currentTarget;
            dropdownButtonRef.current = button;
            const rect = button.getBoundingClientRect();

            const position = calculateDropdownPosition(rect);
            setDropdownPosition(position);
            setOpenDropdownId(sessionId);
        }
    }, [openDropdownId, calculateDropdownPosition]);

    const handleDropdownItemClick = (handler, session) => {
        handler(session);
        setOpenDropdownId(null);
        dropdownButtonRef.current = null;
    };
    const paginate = (pageNumber) => setCurrentPage(pageNumber);
    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearch(search);
        }, 500);

        return () => clearTimeout(timer);
    }, [search]);

    useEffect(() => {
        setCurrentPage(1);
    }, [
        debouncedSearch,
        selectedStartDate,
        selectedEndDate,
        paymentMethodFilter,
        statusFilter,
        insuranceStatusFilter,
        appointmentsPerPage,
    ]);

    useEffect(() => {
        fetchSessions();
    }, [fetchSessions]);

    useEffect(() => {
        const filtered = getFilteredSessions(
            sessions,
            debouncedSearch,
            statusFilter,
            paymentMethodFilter,
            paymentDataBySession,
            false,
            false
        );
        const sorted = getSortedSessions(filtered, sortConfig, paymentDataBySession);
        setPaginateAppointments(sorted);
    }, [
        sessions,
        statusFilter,
        paymentMethodFilter,
        insuranceStatusFilter,
        sortConfig,
        debouncedSearch,
        paymentDataBySession,
    ]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (openDropdownId && !event.target.closest('.session-dropdown-button') && !event.target.closest('.session-dropdown-menu')) {
                setOpenDropdownId(null);
                dropdownButtonRef.current = null;
            }
        };

        const handleKeyDown = (event) => {
            if (event.key === 'Escape' && openDropdownId) {
                setOpenDropdownId(null);
                dropdownButtonRef.current = null;
            }
        };

        let scrollTimeout;
        const handleScroll = () => {
            if (openDropdownId && dropdownButtonRef.current) {
                clearTimeout(scrollTimeout);
                scrollTimeout = setTimeout(() => {
                    const rect = dropdownButtonRef.current.getBoundingClientRect();
                    const position = calculateDropdownPosition(rect);
                    setDropdownPosition(position);
                }, 10);
            }
        };

        const handleResize = () => {
            if (openDropdownId && dropdownButtonRef.current) {
                const rect = dropdownButtonRef.current.getBoundingClientRect();
                const position = calculateDropdownPosition(rect);
                setDropdownPosition(position);
            }
        };

        if (openDropdownId) {
            document.addEventListener('mousedown', handleClickOutside);
            document.addEventListener('keydown', handleKeyDown);
            window.addEventListener('scroll', handleScroll, true);
            window.addEventListener('resize', handleResize);
            return () => {
                clearTimeout(scrollTimeout);
                document.removeEventListener('mousedown', handleClickOutside);
                document.removeEventListener('keydown', handleKeyDown);
                window.removeEventListener('scroll', handleScroll, true);
                window.removeEventListener('resize', handleResize);
            };
        }
    }, [openDropdownId, calculateDropdownPosition]);

    const filteredSessions = getFilteredSessions(
        sessions,
        debouncedSearch,
        statusFilter,
        paymentMethodFilter,
        paymentDataBySession,
        false,
        false
    );
    const sortedSessions = getSortedSessions(filteredSessions, sortConfig, paymentDataBySession);
    const totalPages = Math.ceil(totalSessions / appointmentsPerPage);
    const indexOfFirstSession = totalSessions > 0 ? (currentPage - 1) * appointmentsPerPage + 1 : 0;
    const indexOfLastSession = totalSessions > 0 ? Math.min(currentPage * appointmentsPerPage, totalSessions) : 0;

    return (
        <div className="app-page session-history-container">
            <ToastContainer />
            <Card
                style={{
                    border: "none",
                    borderRadius: "10px",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    height: "100%",
                    overflow: "hidden",
                    maxWidth: "100%",
                    width: "100%",
                    boxSizing: "border-box",
                }}>
                <Card.Body style={{ overflow: "hidden", maxWidth: "100%", padding: "0", width: "100%", boxSizing: "border-box" }}>
                    <div className="app-page-toolbar mb-0" style={{ flexWrap: "nowrap", minWidth: 0 }}>
                        <h3 className="app-page-title">
                            Billing
                        </h3>

                        <SessionFilters
                            search={search}
                            setSearch={setSearch}
                            clearSearchFilter={clearSearchFilter}
                            selectedStartDate={selectedStartDate}
                            selectedEndDate={selectedEndDate}
                            handleRangeSelect={handleRangeSelect}
                            paymentMethodFilter={paymentMethodFilter}
                            setPaymentMethodFilter={setPaymentMethodFilter}
                            statusFilter={statusFilter}
                            setStatusFilter={setStatusFilter}
                            insuranceStatusFilter={insuranceStatusFilter}
                            setInsuranceStatusFilter={setInsuranceStatusFilter}
                            setCurrentPage={setCurrentPage}
                            showBillingDetails={true}
                            showDatePicker={showDatePicker}
                            setShowDatePicker={setShowDatePicker}
                        />
                    </div>

                    <div style={{ width: "100%", maxWidth: "100%", marginTop: "20px", padding: "0", boxSizing: "border-box", minWidth: 0 }}>
                        <SessionsTable
                            sessionsLoading={sessionsLoading}
                            paginateAppointments={paginateAppointments}
                            sessions={sessions}
                            sortConfig={sortConfig}
                            handleSort={handleSort}
                            handleDropdownToggle={handleDropdownToggle}
                            paymentDataBySession={paymentDataBySession}
                            showBillingDetails={true}
                        />
                        {totalSessions > 0 && (
                            <SessionPagination
                                appointmentsPerPage={appointmentsPerPage}
                                setAppointmentsPerPage={setAppointmentsPerPage}
                                setCurrentPage={setCurrentPage}
                                currentPage={currentPage}
                                totalPages={totalPages}
                                totalSessions={totalSessions}
                                indexOfFirstSession={indexOfFirstSession}
                                indexOfLastSession={indexOfLastSession}
                                paginate={paginate}
                            />
                        )}
                    </div>
                </Card.Body>
            </Card>

            <SessionActionsDropdown
                openDropdownId={openDropdownId}
                dropdownPosition={dropdownPosition}
                dropdownMenuRef={dropdownMenuRef}
                paginateAppointments={paginateAppointments}
                handleDropdownItemClick={handleDropdownItemClick}
                handleViewDetails={handleViewDetails}
                paymentDataBySession={paymentDataBySession}
                showBillingDetails={true}
            />

            <SessionDetailsDrawer
                showDetailsDrawer={showDetailsDrawer}
                selectedSession={selectedSession}
                setShowDetailsDrawer={setShowDetailsDrawer}
                setSelectedSession={setSelectedSession}
                paymentDataBySession={paymentDataBySession}
            />
        </div>
    );
};

export default SessionBillingDetails;
